import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { Language, languageNames } from '../i18n';
import { Globe } from 'lucide-react';

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'toggle';
  className?: string;
}

// Language cycle order: English → Chinese → Spanish → English
const languageCycle: Language[] = ['en', 'zh-TW', 'es'];

// Get the next language in the cycle
const getNextLanguage = (currentLang: Language): Language => {
  const currentIndex = languageCycle.indexOf(currentLang);
  const nextIndex = (currentIndex + 1) % languageCycle.length;
  return languageCycle[nextIndex];
};

// Get display text for the next language
const getNextLanguageLabel = (currentLang: Language): string => {
  const nextLang = getNextLanguage(currentLang);
  const labels: Record<Language, string> = {
    en: 'EN',
    'zh-TW': '中文',
    es: 'ES',
  };
  return labels[nextLang];
};

// Get full name for title/aria-label
const getNextLanguageTitle = (currentLang: Language): string => {
  const nextLang = getNextLanguage(currentLang);
  return `Switch to ${languageNames[nextLang]}`;
};

export function LanguageSwitcher({ variant = 'dropdown', className = '' }: LanguageSwitcherProps) {
  const { language, setLanguage } = useLanguage();

  const handleCycle = () => {
    const nextLang = getNextLanguage(language);
    setLanguage(nextLang);
  };

  if (variant === 'toggle') {
    return (
      <button
        onClick={handleCycle}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${className}`}
        title={getNextLanguageTitle(language)}
      >
        <Globe size={18} />
        <span className="text-sm font-medium">
          {getNextLanguageLabel(language)}
        </span>
      </button>
    );
  }

  return (
    <div className={`relative inline-block ${className}`}>
      <select
        value={language}
        onChange={(e) => setLanguage(e.target.value as Language)}
        className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
      >
        <option value="en">{languageNames.en}</option>
        <option value="zh-TW">{languageNames['zh-TW']}</option>
        <option value="es">{languageNames.es}</option>
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
        <Globe size={16} className="text-gray-500 dark:text-gray-400" />
      </div>
    </div>
  );
}

// Compact version for navbar
export function LanguageSwitcherCompact() {
  const { language, setLanguage } = useLanguage();

  const handleClick = () => {
    const nextLang = getNextLanguage(language);
    setLanguage(nextLang);
  };

  return (
    <button
      onClick={handleClick}
      className="p-2 text-secondary-400 hover:text-secondary-500 dark:text-gray-400 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md transition-colors"
      title={getNextLanguageTitle(language)}
      aria-label={getNextLanguageTitle(language)}
    >
      <Globe size={20} />
    </button>
  );
}
